import React from 'react';
import { motion } from 'framer-motion';

const sections = [
  {
    title: '1. Introduction',
    blocks: [
      { type: 'p', text: 'SOLIDCORE VENTURE LLP ("Solidcore Venture", "we", "our", or "us") respects your privacy and is committed to handling your personal information responsibly.' },
      { type: 'p', text: 'This Privacy Policy explains how we collect, use, store and protect information when you visit solidcoreventure.com, contact us or enquire about our services.' },
    ],
  },
  {
    title: '2. Information We Collect',
    blocks: [
      { type: 'p', text: 'When you interact with our website, we may collect information that you voluntarily provide, such as:' },
      {
        type: 'ul',
        items: [
          'Your name and email address.',
          'Your company or business name.',
          'Contact information provided through enquiry forms.',
          'Details about your business, requirements or service enquiries.',
          'Information you voluntarily share in communications with us.',
        ],
      },
      { type: 'p', text: 'We may also receive limited technical information, such as browser type, device information, IP address and basic website usage data, depending on the technologies actually used by the website.' },
    ],
  },
  {
    title: '3. How We Use Your Information',
    blocks: [
      { type: 'p', text: 'We use the information collected to:' },
      {
        type: 'ul',
        items: [
          'Respond to enquiries and communicate with prospective or existing clients.',
          'Understand business requirements and evaluate potential service engagements.',
          'Provide advertising, marketing, market research and business consultancy services.',
          'Maintain client communications and business records.',
          'Improve website functionality, reliability and security.',
          'Comply with applicable legal obligations.',
        ],
      },
      { type: 'p', text: 'We do not sell personal information to third parties.' },
    ],
  },
  {
    title: '4. Information Sharing',
    blocks: [
      { type: 'p', text: 'We may share information with service providers that support our website hosting, enquiry processing, communications or other business operations, where necessary.' },
      { type: 'p', text: 'We may also disclose information when required by applicable law or lawful requests from competent authorities.' },
      { type: 'p', text: 'We do not disclose personal information to unrelated third parties for their independent marketing purposes without an appropriate legal basis.' },
    ],
  },
  {
    title: '5. Cookies and Website Technologies',
    blocks: [
      { type: 'p', text: 'Our website may use cookies or similar technologies to support essential functionality and, where implemented, understand website performance.' },
      { type: 'p', text: 'Where optional analytics or other non-essential tracking technologies are used, we will explain their purpose and provide applicable choices or consent controls.' },
      { type: 'p', text: 'Visitors may also manage cookies through their browser settings.' },
    ],
  },
  {
    title: '6. Data Retention',
    blocks: [
      { type: 'p', text: 'We retain personal information only for as long as reasonably necessary for the purposes for which it was collected, including legitimate business requirements, contractual obligations and applicable legal requirements.' },
      { type: 'p', text: 'Information that is no longer required will be deleted or appropriately anonymised, subject to applicable retention obligations.' },
    ],
  },
  {
    title: '7. Data Security',
    blocks: [
      { type: 'p', text: 'We take reasonable technical and organisational measures to protect personal information against unauthorised access, loss, misuse or disclosure.' },
      { type: 'p', text: 'However, no internet-based service can guarantee absolute security.' },
    ],
  },
  {
    title: '8. Your Privacy Choices and Rights',
    blocks: [
      { type: 'p', text: 'Subject to applicable law, you may request information about personal data held by us, ask for corrections, request deletion where appropriate, or withdraw consent for processing based on consent.' },
      { type: 'p', text: 'To submit a privacy-related request, contact:' },
      { type: 'contact', email: 'kuzagra@solidcoreventure.com' },
      { type: 'p', text: 'We may need to verify your identity before processing certain requests.' },
    ],
  },
  {
    title: '9. Third-Party Services',
    blocks: [
      { type: 'p', text: 'Our website may integrate with external service providers for hosting, communications, form processing or other essential business functions.' },
      { type: 'p', text: 'These providers may process information according to their own privacy practices and their agreements with us.' },
      { type: 'p', text: 'Where relevant, their use and any international processing of personal information will be disclosed consistently with applicable legal requirements.' },
    ],
  },
  {
    title: '10. Changes to This Privacy Policy',
    blocks: [
      { type: 'p', text: 'We may update this Privacy Policy to reflect changes in our services, technologies, operating practices or applicable law.' },
      { type: 'p', text: 'The updated version will be published on this page with a revised effective date.' },
    ],
  },
  {
    title: '11. Contact Us',
    blocks: [
      { type: 'p', text: 'For questions concerning this Privacy Policy or the handling of your personal information, contact:' },
      { type: 'entity' },
    ],
  },
];

const PrivacyPolicy = () => {
  return (
    <div style={{ background: 'var(--ink)', minHeight: '100vh' }} data-testid="privacy-page">
      {/* Header */}
      <section className="relative overflow-hidden" style={{ padding: '160px 0 70px' }}>
        <div
          className="glow"
          style={{
            width: 700, height: 700, left: '50%', top: -380,
            transform: 'translateX(-50%)',
            background: 'radial-gradient(circle, rgba(70,110,230,0.3), transparent 65%)',
          }}
        />
        <div className="relative max-w-[820px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="eyebrow">Privacy</span>
            <h1
              style={{
                fontFamily: 'var(--display)',
                fontSize: 'clamp(36px, 5vw, 60px)',
                fontWeight: 900,
                letterSpacing: '-0.03em',
                lineHeight: 1.0,
                color: 'var(--text)',
              }}
            >
              Privacy Policy
            </h1>
            <p className="font-mono mt-5" style={{ fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--muted)' }}>
              Effective Date: 21 October 2026
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section
        className="cream"
        style={{ background: 'var(--cream)', color: 'var(--char)', padding: '70px 0 110px' }}
      >
        <div className="max-w-[820px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="space-y-12"
          >
            {sections.map((section) => (
              <div key={section.title}>
                <h2
                  style={{
                    fontFamily: 'var(--display)',
                    fontSize: 'clamp(21px, 2.4vw, 26px)',
                    fontWeight: 800,
                    letterSpacing: '-0.02em',
                    color: 'var(--char)',
                    marginBottom: 14,
                  }}
                >
                  {section.title}
                </h2>
                <div className="space-y-4">
                  {section.blocks.map((block, i) => {
                    if (block.type === 'p') {
                      return (
                        <p key={i} style={{ fontSize: 16, color: 'var(--char-2)', lineHeight: 1.75 }}>
                          {block.text}
                        </p>
                      );
                    }
                    if (block.type === 'ul') {
                      return (
                        <ul key={i} className="space-y-2 pl-1">
                          {block.items.map((item) => (
                            <li
                              key={item}
                              className="flex gap-3 items-start"
                              style={{ fontSize: 16, color: 'var(--char-2)', lineHeight: 1.7 }}
                            >
                              <span style={{ color: '#B07A16', fontWeight: 700, flex: 'none', marginTop: 2 }}>·</span>
                              {item}
                            </li>
                          ))}
                        </ul>
                      );
                    }
                    if (block.type === 'contact') {
                      return (
                        <p key={i}>
                          <a
                            href={`mailto:${block.email}`}
                            style={{ color: '#A36F12', fontWeight: 600, fontSize: 16 }}
                            data-testid="privacy-contact-email"
                          >
                            {block.email}
                          </a>
                        </p>
                      );
                    }
                    if (block.type === 'entity') {
                      return (
                        <div
                          key={i}
                          className="rounded-[14px] p-6"
                          style={{ background: 'var(--paper)', border: '1px solid #E2D9C7', fontSize: 16, lineHeight: 1.8, color: 'var(--char)' }}
                        >
                          <div style={{ fontWeight: 700 }}>SOLIDCORE VENTURE LLP</div>
                          <div>
                            Email:{' '}
                            <a href="mailto:kuzagra@solidcoreventure.com" style={{ color: '#A36F12', fontWeight: 600 }}>
                              kuzagra@solidcoreventure.com
                            </a>
                          </div>
                          <div>
                            Website:{' '}
                            <a href="https://solidcoreventure.com/" style={{ color: '#A36F12', fontWeight: 600 }}>
                              https://solidcoreventure.com/
                            </a>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
